/**
 * 문의 대화의 첨부 사진 한 장 — 본부(HQ) · 원장 문의 화면 공용 (2026-10-14)
 *
 * 🔴 왜 따로 만들었나: 대화를 열면 그 매장의 지난 사진을 **전부 한꺼번에** 받았다.
 *    10/14 19:19 본부 화면이 사진 127건을 동시에 요청 → 서버 일꾼 하나의 DB 연결이 고갈돼
 *    93초 멈췄고, 그 일꾼으로 간 학생 입실 전송이 실패해 학부모 알림이 빠졌다(송파 김나린).
 *
 * 규칙
 *  - 화면에 보일 때만 받는다 (IntersectionObserver). 위로 스크롤해야 받는다.
 *  - 7일 지난 사진은 자동으로 받지 않는다 — [사진 보기] 를 눌러야 받는다.
 *  - 같은 사진은 한 번만 받는다 (모듈 전체 캐시 · 받는 중 요청도 공유).
 *
 * 사진 자체는 지우지 않는다(되돌릴 수 없음). 받는 시점만 늦춘다.
 */
import React, { useEffect, useRef, useState } from 'react';

const OLD_DAYS = 7;

// 받은 사진(blob URL)과 받는 중인 요청 — 화면을 다시 그려도 중복 요청이 안 나가게
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

type Props = {
  id: string;
  filename?: string | null;
  /** 이 사진이 달린 메시지 작성 시각 (ISO) — 오래된 사진 판정용 */
  createdAt?: string | null;
  /** 사진을 받아 blob URL 을 돌려주는 함수 (인증 방식이 화면마다 달라 밖에서 넘긴다) */
  fetchUrl: (id: string) => Promise<string | null>;
  className?: string;
};

const load = (id: string, fetchUrl: Props['fetchUrl']): Promise<string | null> => {
  const hit = cache.get(id);
  if (hit) return Promise.resolve(hit);
  let p = inflight.get(id);
  if (!p) {
    p = fetchUrl(id)
      .then((url) => {
        if (url) cache.set(id, url);
        return url;
      })
      .catch(() => null)
      .finally(() => inflight.delete(id));
    inflight.set(id, p);
  }
  return p;
};

const isOld = (iso?: string | null) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t > OLD_DAYS * 24 * 60 * 60 * 1000;
};

const SupportAttachmentImage: React.FC<Props> = ({ id, filename, createdAt, fetchUrl, className }) => {
  const [url, setUrl] = useState<string | null>(() => cache.get(id) || null);
  const [wanted, setWanted] = useState<boolean>(() => !isOld(createdAt));
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // 화면에 들어왔는지 감시 (한 번 들어오면 끝)
  useEffect(() => {
    if (url || visible) return;
    const el = boxRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [url, visible]);

  useEffect(() => {
    if (url || failed || !wanted || !visible) return;
    let alive = true;
    load(id, fetchUrl).then((u) => {
      if (!alive) return;
      if (u) setUrl(u);
      else setFailed(true);
    });
    return () => {
      alive = false;
    };
  }, [id, url, failed, wanted, visible, fetchUrl]);

  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={filename || '첨부'} className={className || 'rounded-lg max-h-64 border border-black/10'} />
      </a>
    );
  }

  return (
    <div ref={boxRef} className="text-xs opacity-80">
      {!wanted ? (
        <button
          type="button"
          onClick={() => {
            setWanted(true);
            setVisible(true);
          }}
          className="underline"
        >
          📷 사진 보기{filename ? ` (${filename})` : ''}
        </button>
      ) : failed ? (
        // 서버가 바쁘면 사진 요청을 잠시 거절한다(503) — 눌러서 다시 받는다
        <button type="button" onClick={() => setFailed(false)} className="underline">
          📷 사진을 불러오지 못했습니다 · 다시 보기
        </button>
      ) : (
        <span>📷 {filename || '이미지'} 불러오는 중…</span>
      )}
    </div>
  );
};

export default SupportAttachmentImage;
